/**
 * What the help menu offers, as data, so its search and its "What's new" list
 * can be tested without rendering.
 *
 * @module components/helpMenu.logic
 */

export type HelpDestination =
  | { readonly kind: "external"; readonly href: string }
  | { readonly kind: "route"; readonly path: (org: string | null) => string }
  | { readonly kind: "action"; readonly action: "guide" | "search" };

export interface HelpEntry {
  readonly id: string;
  readonly label: string;
  /** Extra words the search should find this by. */
  readonly keywords: string;
  readonly destination: HelpDestination;
  /** A rebindable command whose current key is shown on the row. */
  readonly shortcutCommand?: "guide.toggle" | "commandPalette.toggle";
}

export const DOCS_URL = "https://docs.amoreg.com";
export const CONTACT_EMAIL = "hello@amoreg.com";
export const STATUS_URL = "https://status.amoreg.com";

const orgPath = (suffix: string, fallback: string) => (org: string | null) =>
  org ? `/${org}${suffix}` : fallback;

export const HELP_ENTRIES: ReadonlyArray<HelpEntry> = [
  {
    id: "docs",
    label: "Docs",
    keywords: "documentation help manual quickstart read",
    destination: { kind: "external", href: DOCS_URL },
  },
  {
    id: "guide",
    label: "Ask the guide",
    keywords: "ask ai how do i where is help assistant",
    destination: { kind: "action", action: "guide" },
    shortcutCommand: "guide.toggle",
  },
  {
    id: "search",
    label: "Search everything",
    keywords: "find jump command palette",
    destination: { kind: "action", action: "search" },
    shortcutCommand: "commandPalette.toggle",
  },
  {
    id: "shortcuts",
    label: "Keyboard shortcuts",
    keywords: "keys keybindings hotkeys rebind",
    destination: { kind: "route", path: orgPath("/settings/keybindings", "/settings/keybindings") },
  },
  {
    id: "contact",
    label: "Contact us",
    keywords: "support email feedback bug problem",
    destination: { kind: "external", href: `mailto:${CONTACT_EMAIL}` },
  },
  {
    id: "status",
    label: "Amoreg status",
    keywords: "uptime incident outage down health",
    destination: { kind: "external", href: STATUS_URL },
  },
  {
    id: "settings",
    label: "Settings",
    keywords: "preferences account appearance theme team",
    destination: { kind: "route", path: orgPath("/settings/general", "/settings/general") },
  },
];

/**
 * Recently shipped, user-visible work, newest first. Hand-kept on purpose:
 * there is no changelog service, and an invented entry would be worse than a
 * short list. Each entry names something real and links to where it lives.
 */
export const WHATS_NEW: ReadonlyArray<HelpEntry> = [
  {
    id: "new-eve-operates",
    label: "Eve can run your workspace",
    keywords: "eve regimes policies invite members context new",
    destination: { kind: "route", path: orgPath("", "/") },
  },
  {
    id: "new-inspector",
    label: "Inspector: filter, sort, schedule",
    keywords: "inspector sweep schedule filter sort new",
    destination: { kind: "route", path: orgPath("/compliance/inspector", "/compliance") },
  },
  {
    id: "new-team",
    label: "Members: a searchable table, one sheet per person",
    keywords: "team members invite role allowance usage limits new",
    destination: { kind: "route", path: orgPath("/settings/members", "/settings/members") },
  },
];

/** Every word of the query must appear in the label or keywords, in any order. */
export function searchHelp(query: string): HelpEntry[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  const needle = tokens.join(" ");
  // A row's name beats its hidden keywords: "guide" should find "Ask the
  // guide" before Docs, which merely lists the word.
  const rank = (entry: HelpEntry) => {
    const label = entry.label.toLowerCase();
    if (label.startsWith(needle)) return 0;
    return tokens.every((token) => label.includes(token)) ? 1 : 2;
  };
  return [...HELP_ENTRIES, ...WHATS_NEW]
    .filter((entry) => {
      const haystack = `${entry.label} ${entry.keywords}`.toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    })
    .toSorted((left, right) => rank(left) - rank(right));
}
